import React, { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Header, Grid, Card, Icon, Button, Accordion } from 'semantic-ui-react';
import MultiplyHeader from '../components/MultiplyHeader/MultiplyHeader';
import { componentContainerStyle, componentContentStyle } from '../constants';
import { calculateUserAchievements } from '../utils/achievementCalculation';
import AchievementNotification from '../components/AchievementNotification/AchievementNotification';
import ProgressChart from '../components/Dashboard/ProgressChart';
import GoalProgress from '../components/Dashboard/GoalProgress';
import DashboardNudges from '../components/Dashboard/DashboardNudges';
import {
    filterBySessionType, getBorderColor, getLightBackgroundColor,
    STREAK_MESSAGES, NO_STREAK_MESSAGES
} from './dashboardHelpers';
import NewAssessmentDetails from '../components/PerformanceReviews/NewAssessmentDetails';

// --- Component ---

const DashboardPage = observer(({ usersStore, companyStore, assessmentsStore, meetingStore, history }) => {
    const [availableAchievementsOpen, setAvailableAchievementsOpen] = useState(false);
    const [expandedFeedbackSection, setExpandedFeedbackSection] = useState(null);
    const [newAchievement, setNewAchievement] = useState(null);
    const [showAchievementNotification, setShowAchievementNotification] = useState(false);
    // Function to handle recording start - mimics RecordPresentationSelection logic
    const startRecording = async (sessionType, isInteractive = false) => {
        // Map session types to recording keys based on the meetingTypes structure
        const recordingKeyMap = {
            'Elevator Pitch': isInteractive ? 'elevator_pitch_2' : 'elevator_pitch',
            'Casual Encounter': isInteractive ? 'casual_encounter_2' : 'casual_encounter', 
            'Sales Presentation': isInteractive ? 'sales_presentation_2' : 'sales_presentation'
        };

        const recordingKey = recordingKeyMap[sessionType];
        if (recordingKey) {
            await meetingStore.setMeetingType(recordingKey, companyStore.company.id);
            history.push(`/record-presentation/${recordingKey}`);
        }
    };

    const currentUser = usersStore?.currentUser;

    // Determine loading/ready state (no early returns before hooks)
    const isStoreLoading = usersStore?.isLoading || assessmentsStore?.isLoading;
    const isDataReady = usersStore && currentUser && currentUser.userId;

    // Add safety checks for assessmentsStore calls
    let currentUserStreakData = null;
    let isOnStreak = false;
    let userAssessments = [];
    let achievements = [];
    
    try {
        if (usersStore?.getCurrentUserStreakStatus && assessmentsStore) {
            currentUserStreakData = usersStore.getCurrentUserStreakStatus(assessmentsStore);
        }
        if (usersStore?.isUserOnStreak && assessmentsStore && currentUser?.userId) {
            isOnStreak = usersStore.isUserOnStreak(currentUser.userId, assessmentsStore);
        }

        // Get user assessments and calculate achievements
        if (assessmentsStore?.closedAssessmentsForUser && currentUser?.userId) {
            userAssessments = assessmentsStore.closedAssessmentsForUser(currentUser.userId);
            achievements = calculateUserAchievements(userAssessments, currentUserStreakData);
        }
    } catch (error) {
        console.error('Error loading user data:', error);
    }

    // Check for new achievements
    useEffect(() => {
        if (!achievements?.earned || !currentUser?.userId) return;

        const previousEarnedIds = JSON.parse(localStorage.getItem(`earnedAchievements_${currentUser.userId}`)) || [];
        const currentEarnedIds = achievements.earned.map(a => a.id);

        const newlyEarned = achievements.earned.filter(achievement =>
            !previousEarnedIds.includes(achievement.id)
        );

        if (newlyEarned.length > 0) {
            setNewAchievement(newlyEarned[0]);
            setShowAchievementNotification(true);
            localStorage.setItem(`earnedAchievements_${currentUser.userId}`, JSON.stringify(currentEarnedIds));
        }
    }, [achievements.earned, currentUser?.userId]);

    // Motivational messages - memoized to avoid changing on every re-render
    const streakMotivation = useMemo(() => STREAK_MESSAGES[Math.floor(Math.random() * STREAK_MESSAGES.length)], []);
    const noStreakMotivation = useMemo(() => NO_STREAK_MESSAGES[Math.floor(Math.random() * NO_STREAK_MESSAGES.length)], []);

    if (isStoreLoading || !isDataReady) {
        return (
            <div style={componentContainerStyle}>
                <MultiplyHeader />
                <div style={componentContentStyle}>
                    <div style={{ marginTop: '2em', textAlign: 'center' }}>
                        <Header as="h2" textAlign="center">
                            {isStoreLoading ? 'Loading application data...' : 'Loading user data...'}
                        </Header>
                        <div style={{ marginTop: '1em', color: '#666' }}>
                            Please wait while we load your data...
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={componentContainerStyle}>
            <MultiplyHeader text="Dashboard" />
            <div style={componentContentStyle}>
                <DashboardNudges
                    assessments={userAssessments}
                    streakData={currentUserStreakData || {}}
                    goals={companyStore?.company?.practiceGoals}
                    onStartRecording={(sessionType) => startRecording(sessionType || 'Elevator Pitch')}
                />
                <Header
                    as="h2"
                    style={{
                        color: '#137fa2',
                        fontFamily: 'Montserrat',
                        fontSize: '20px',
                        fontWeight: 600,
                        marginBottom: '8px',
                        marginTop: '0px'
                    }}
                >
                    Welcome to your Dashboard, {currentUser.firstName}
                </Header>
                
                <div 
                    style={{ 
                        fontFamily: 'Montserrat', 
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#666',
                        marginBottom: '20px',
                        lineHeight: '1.4'
                    }}
                >
                    {isOnStreak ? (
                        <div>
                            🔥 You are on an impressive <strong style={{ color: '#137fa2' }}>{currentUserStreakData.currentStreak} week streak</strong>! Keep it going! <em>{streakMotivation}</em>
                        </div>
                    ) : (
                        <div>
                            📈 Log a practice to begin your next streak! <em>{noStreakMotivation}</em>
                        </div>
                    )}
                </div>

                {/* Training Hub Section */}
                <Header
                    as="h3"
                    style={{
                        color: '#137fa2',
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 600,
                        marginBottom: '15px',
                        marginTop: '30px'
                    }}
                >
                    Training Hub
                </Header>

                <Grid stackable columns={3} style={{ marginBottom: '20px' }}>
                    {['Elevator Pitch', 'Casual Encounter', 'Sales Presentation'].map((sessionType, index) => {
                        const typeAssessments = filterBySessionType(userAssessments, sessionType);

                        const lastCompleted = typeAssessments.length > 0
                            ? [...typeAssessments].sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime))[0]
                            : null;

                        const totalPractices = typeAssessments.length;
                        const lastCompletedDate = lastCompleted ? new Date(lastCompleted.completedDateTime) : null;

                        // Get colors for each type
                        const colors = {
                            'Elevator Pitch': '#dc3545', // red
                            'Casual Encounter': '#dc3545', // red
                            'Sales Presentation': '#6c757d' // gray
                        };

                        const cardColor = colors[sessionType];
                        const borderColor = getBorderColor(sessionType, userAssessments);

                        // Calculate actual status based on last completion date
                        let statusText = 'Not Started';
                        if (lastCompleted && lastCompletedDate) {
                            const now = new Date();
                            const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                            const lastMid = new Date(lastCompletedDate.getFullYear(), lastCompletedDate.getMonth(), lastCompletedDate.getDate());
                            const daysDiff = Math.round((todayMid - lastMid) / (1000 * 60 * 60 * 24));

                            if (daysDiff === 0) {
                                statusText = 'Completed Today';
                            } else if (daysDiff === 1) {
                                statusText = 'Completed Yesterday';
                            } else if (daysDiff <= 7) {
                                statusText = `Completed ${daysDiff} days ago`;
                            } else if (daysDiff <= 30) {
                                statusText = `Completed ${daysDiff} days ago`;
                            } else {
                                statusText = `Completed ${Math.floor(daysDiff / 30)} month${Math.floor(daysDiff / 30) > 1 ? 's' : ''} ago`;
                            }
                        }

                        const backgroundColor = getLightBackgroundColor(borderColor);

                        return (
                            <Grid.Column key={sessionType} style={{ minWidth: '350px' }}>
                                <Card
                                    style={{
                                        border: `2px solid ${borderColor}`,
                                        backgroundColor: backgroundColor,
                                        borderRadius: '8px',
                                        height: 'auto',
                                        minHeight: '250px',
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <Card.Content style={{
                                        padding: '15px',
                                        flex: '1',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <Icon
                                                    name={sessionType === 'Elevator Pitch' ? 'phone' : sessionType === 'Casual Encounter' ? 'users' : 'desktop'}
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        color: '#0784a7',
                                                        padding: '4px',
                                                        borderRadius: '4px',
                                                        marginRight: '8px',
                                                        fontSize: '20px',
                                                        width: '28px',
                                                        height: '28px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                />
                                                <div>
                                                    <div style={{
                                                        fontFamily: 'Montserrat',
                                                        fontWeight: 600,
                                                        fontSize: '14px',
                                                        color: '#333'
                                                    }}>
                                                        {sessionType}
                                                    </div>
                                                    <div style={{
                                                        fontFamily: 'Montserrat',
                                                        fontSize: '11px',
                                                        color: '#666'
                                                    }}>
                                                        {sessionType === 'Elevator Pitch'
                                                            ? 'Perfect your 30-second introduction'
                                                            : sessionType === 'Casual Encounter'
                                                            ? 'Master informal networking conversations'
                                                            : 'Excel in formal business presentations'
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{
                                                backgroundColor: statusText === 'Completed Today' ? '#28a745' : statusText === 'Completed Yesterday' ? '#ffc107' : '#6c757d',
                                                color: 'white',
                                                padding: '4px 8px',
                                                borderRadius: '12px',
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                display: 'inline-block',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {statusText}
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '15px', marginTop: '15px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{
                                                        fontSize: '18px',
                                                        fontWeight: 'bold',
                                                        color: cardColor,
                                                        fontFamily: 'Montserrat'
                                                    }}>
                                                        {totalPractices}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '9px',
                                                        color: '#666',
                                                        fontFamily: 'Montserrat'
                                                    }}>
                                                        Total Sessions
                                                    </div>
                                                </div>

                                                {(() => {
                                                    const typeAssessments = filterBySessionType(userAssessments, sessionType);

                                                    // Count standard vs interactive recordings from actual data
                                                    const recordingKeyMap = {
                                                        'Elevator Pitch': { standard: 'elevator_pitch', interactive: 'elevator_pitch_2' },
                                                        'Casual Encounter': { standard: 'casual_encounter', interactive: 'casual_encounter_2' },
                                                        'Sales Presentation': { standard: 'sales_presentation', interactive: 'sales_presentation_2' }
                                                    };

                                                    const sessionKeys = recordingKeyMap[sessionType];
                                                    let standardCount = 0;
                                                    let interactiveCount = 0;

                                                    if (sessionKeys) {
                                                        typeAssessments.forEach(a => {
                                                            if (a.recordingKey && a.recordingKey.endsWith('_2')) {
                                                                interactiveCount++;
                                                            } else {
                                                                standardCount++;
                                                            }
                                                        });
                                                    }

                                                    return (
                                                        <>
                                                            <div style={{ textAlign: 'center' }}>
                                                                <div style={{
                                                                    fontSize: '16px',
                                                                    fontWeight: 'bold',
                                                                    color: '#333',
                                                                    fontFamily: 'Montserrat'
                                                                }}>
                                                                    {standardCount}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '9px',
                                                                    color: '#666',
                                                                    fontFamily: 'Montserrat'
                                                                }}>
                                                                    Standard
                                                                </div>
                                                            </div>

                                                            <div style={{ textAlign: 'center' }}>
                                                                <div style={{
                                                                    fontSize: '16px',
                                                                    fontWeight: 'bold',
                                                                    color: '#333',
                                                                    fontFamily: 'Montserrat'
                                                                }}>
                                                                    {interactiveCount}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '9px',
                                                                    color: '#666',
                                                                    fontFamily: 'Montserrat'
                                                                }}>
                                                                    Interactive
                                                                </div>
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                                            <Button
                                                size="small"
                                                style={{
                                                    backgroundColor: '#0784a7',
                                                    color: 'white',
                                                    fontSize: '10px',
                                                    padding: '8px 12px',
                                                    flex: 1,
                                                    border: 'none',
                                                    transition: 'all 0.2s ease',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#0a94bc';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#0784a7';
                                                }}
                                                onClick={() => startRecording(sessionType, false)}
                                            >
                                                Record Standard
                                            </Button>
                                            <Button
                                                size="small"
                                                style={{
                                                    backgroundColor: '#0784a7',
                                                    color: 'white',
                                                    fontSize: '10px',
                                                    padding: '8px 12px',
                                                    flex: 1,
                                                    border: 'none',
                                                    transition: 'all 0.2s ease',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#0a94bc';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#0784a7';
                                                }}
                                                onClick={() => startRecording(sessionType, true)}
                                            >
                                                Record Interactive
                                            </Button>
                                        </div>

                                        <Accordion style={{ border: 'none', boxShadow: 'none' }}>
                                            <Accordion.Title
                                                active={expandedFeedbackSection === sessionType}
                                                onClick={() => {
                                                    // Toggle the expanded feedback section
                                                    setExpandedFeedbackSection(
                                                        expandedFeedbackSection === sessionType ? null : sessionType
                                                    );
                                                }}
                                                style={{
                                                    fontFamily: 'Montserrat',
                                                    fontSize: '10px',
                                                    color: cardColor,
                                                    padding: '8px 15px',
                                                    cursor: 'pointer',
                                                    textAlign: 'center',
                                                    borderTop: `1px solid ${cardColor}20`,
                                                    margin: '0 -15px -15px -15px',
                                                    marginTop: 'auto'
                                                }}
                                            >
                                                <Icon name='dropdown' />
                                                View All {sessionType} Feedback
                                            </Accordion.Title>
                                        </Accordion>
                                    </Card.Content>
                                </Card>
                            </Grid.Column>
                        );
                    })}
                </Grid>

                {/* Expanded Feedback Section - Full Width */}
                {expandedFeedbackSection && (() => {
                    const typeAssessments = filterBySessionType(userAssessments, expandedFeedbackSection)
                        .sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime));

                    return (
                        <div style={{
                            marginTop: '30px',
                            padding: '20px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <Header as="h3" style={{
                                    fontFamily: 'Montserrat',
                                    color: '#2c3e50',
                                    margin: 0
                                }}>
                                    {expandedFeedbackSection} Feedback
                                </Header>
                                <Button
                                    icon="close"
                                    size="small"
                                    style={{ backgroundColor: 'transparent', color: '#666' }}
                                    onClick={() => setExpandedFeedbackSection(null)}
                                />
                            </div>
                            {typeAssessments.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                    <Icon name="clipboard outline" size="huge" style={{ color: '#ddd' }} />
                                    <p>No {expandedFeedbackSection.toLowerCase()} assessments completed yet.</p>
                                    <p>Complete your first assessment to see your feedback here!</p>
                                </div>
                            ) : (
                                <NewAssessmentDetails assessments={typeAssessments} />
                            )}
                        </div>
                    );
                })()}

                <GoalProgress assessments={userAssessments} companyStore={companyStore} />
                <ProgressChart assessments={userAssessments} />

                <Header
                    as="h3"
                    style={{
                        color: '#137fa2',
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 600,
                        marginBottom: '15px',
                        marginTop: '10px'
                    }}
                >
                    🏆 Your Achievements ({achievements.totalEarned})
                </Header>

                {/* Earned Achievement Badges */}
                <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '12px', 
                    marginBottom: '20px' 
                }}>
                    {achievements.earned.map((achievement, index) => (
                        <div 
                            key={achievement.id} 
                            style={{ 
                                border: `2px solid ${achievement.color}`,
                                borderRadius: '10px',
                                textAlign: 'center',
                                width: '90px',
                                height: '100px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: `linear-gradient(135deg, #fff 0%, ${achievement.color}10 100%)`,
                                padding: '8px',
                                boxSizing: 'border-box',
                                flexShrink: 0,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                transition: 'transform 0.2s ease',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0px)'}
                        >
                            <div style={{ 
                                fontSize: '20px', 
                                backgroundColor: achievement.color,
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: `0 2px 6px ${achievement.color}40`,
                                marginTop: '2px'
                            }}>
                                <span style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
                                    {achievement.icon}
                                </span>
                            </div>
                            <div style={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                                textAlign: 'center'
                            }}>
                                <div style={{ 
                                    fontFamily: 'Montserrat',
                                    fontWeight: 700,
                                    fontSize: '9px',
                                    color: achievement.color,
                                    marginBottom: '2px',
                                    lineHeight: '1.1',
                                    wordWrap: 'break-word',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    {achievement.name}
                                </div>
                                <div style={{ 
                                    fontFamily: 'Montserrat',
                                    fontSize: '7px',
                                    color: '#555',
                                    lineHeight: '1.2',
                                    wordWrap: 'break-word',
                                    textAlign: 'center',
                                    fontWeight: 500
                                }}>
                                    {achievement.description}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Available Achievements Dropdown */}
                <Accordion style={{ marginTop: '10px' }}>
                    <Accordion.Title
                        active={availableAchievementsOpen}
                        onClick={() => setAvailableAchievementsOpen(!availableAchievementsOpen)}
                        style={{
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#137fa2',
                            padding: '10px 0',
                            cursor: 'pointer'
                        }}
                    >
                        <Icon name='dropdown' />
                        Available Achievements ({achievements.totalAvailable})
                    </Accordion.Title>
                    <Accordion.Content active={availableAchievementsOpen}>
                        <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '12px', 
                            marginTop: '10px' 
                        }}>
                            {achievements.available.map((achievement) => (
                                <div 
                                    key={achievement.id} 
                                    style={{ 
                                        border: '2px dashed #d0d0d0',
                                        borderRadius: '10px',
                                        textAlign: 'center',
                                        width: '90px',
                                        height: '100px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        background: 'linear-gradient(135deg, #f8f8f8 0%, #f0f0f0 100%)',
                                        opacity: 0.6,
                                        padding: '8px',
                                        boxSizing: 'border-box',
                                        flexShrink: 0,
                                        position: 'relative'
                                    }}
                                >
                                    <div style={{ 
                                        fontSize: '20px', 
                                        backgroundColor: '#d0d0d0',
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        marginTop: '2px',
                                        position: 'relative'
                                    }}>
                                        <span style={{ color: '#999', fontSize: '18px', opacity: 0.7 }}>
                                            {achievement.icon}
                                        </span>
                                        <div style={{
                                            position: 'absolute',
                                            top: '-2px',
                                            right: '-2px',
                                            width: '16px',
                                            height: '16px',
                                            backgroundColor: '#666',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '10px',
                                            color: 'white'
                                        }}>
                                            🔒
                                        </div>
                                    </div>
                                    <div style={{ 
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flex: 1,
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ 
                                            fontFamily: 'Montserrat',
                                            fontWeight: 600,
                                            fontSize: '9px',
                                            color: '#888',
                                            marginBottom: '2px',
                                            lineHeight: '1.1',
                                            wordWrap: 'break-word',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px'
                                        }}>
                                            {achievement.name}
                                        </div>
                                        <div style={{ 
                                            fontFamily: 'Montserrat',
                                            fontSize: '7px',
                                            color: '#aaa',
                                            lineHeight: '1.2',
                                            wordWrap: 'break-word',
                                            textAlign: 'center',
                                            fontWeight: 400
                                        }}>
                                            {achievement.description}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Accordion.Content>
                </Accordion>


            </div>
            
            {/* Achievement Notification Popup */}
            <AchievementNotification
                achievement={newAchievement}
                isVisible={showAchievementNotification}
                onClose={() => {
                    setShowAchievementNotification(false);
                    setNewAchievement(null);
                }}
            />
        </div>
    );
});

export default DashboardPage;